
import React, { useState, useEffect } from "react";
import { 
  Table, TableHeader, TableBody, TableRow, 
  TableHead, TableCell, TableCaption 
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CustomBadge } from "./utils/BadgeVariants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LayoutLoader } from "@/components/ui/layout-loader";
import { useToast } from "@/components/ui/use-toast";
import { 
  Tag, 
  Award, 
  Search, 
  Plus, 
  CalendarClock, 
  Percent
} from "lucide-react";
import { AngkasaPromotion, getAllPromotions, savePromotion } from "@/lib/db/angkasaMemberDb";

const MemberPromotionsTab = () => {
  const [promotions, setPromotions] = useState<AngkasaPromotion[]>([]);
  const [filteredPromotions, setFilteredPromotions] = useState<AngkasaPromotion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [coopFilter, setCoopFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isAddPromoOpen, setIsAddPromoOpen] = useState(false);
  const { toast } = useToast();

  const form = useForm({
    defaultValues: {
      id: `PROMO-${Math.floor(1000 + Math.random() * 9000)}`,
      name: "",
      description: "",
      startDate: new Date().toISOString().substring(0, 10),
      endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().substring(0, 10),
      cooperativeName: "",
      discountType: "Percentage",
      discountValue: 10,
      minimumSpend: 100,
      productCategory: "",
      maxRedemptions: 1000,
      currentRedemptions: 0,
      memberGroup: ""
    },
  });

  useEffect(() => {
    const fetchPromotions = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getAllPromotions();
        console.log("Fetched promotions:", data);
        setPromotions(data);
        setFilteredPromotions(data);
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to fetch promotions", error);
        setError("Failed to load promotion data. Please try refreshing the page.");
        toast({
          variant: "destructive",
          title: "Fetch Error",
          description: "Failed to fetch promotion data.",
        });
        setIsLoading(false);
      }
    };

    fetchPromotions();
  }, [toast]);

  useEffect(() => {
    filterPromotions();
  }, [promotions, searchTerm, coopFilter, statusFilter]);

  const filterPromotions = () => {
    let filtered = [...promotions];

    // Apply search term filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(promo => 
        promo.name.toLowerCase().includes(searchLower) ||
        promo.description.toLowerCase().includes(searchLower)
      );
    }

    // Apply cooperative filter
    if (coopFilter && coopFilter !== "all") {
      filtered = filtered.filter(promo => 
        promo.cooperativeName === coopFilter || 
        (coopFilter === "null" && promo.cooperativeName === null)
      );
    }

    // Apply status filter (active/expired/upcoming)
    if (statusFilter !== "all") {
      const today = new Date().toISOString().split('T')[0];
      if (statusFilter === "active") {
        filtered = filtered.filter(promo => 
          promo.startDate <= today && promo.endDate >= today
        );
      } else if (statusFilter === "expired") {
        filtered = filtered.filter(promo => promo.endDate < today);
      } else if (statusFilter === "upcoming") {
        filtered = filtered.filter(promo => promo.startDate > today);
      }
    }

    setFilteredPromotions(filtered);
  };

  const onSubmit = async (data: any) => {
    try {
      const newPromotion: AngkasaPromotion = {
        ...data,
        cooperativeName: data.cooperativeName === "" ? null : data.cooperativeName,
        productCategory: data.productCategory === "" ? null : data.productCategory,
        memberGroup: data.memberGroup === "" ? null : data.memberGroup,
      };
      
      await savePromotion(newPromotion);
      toast({
        title: "Promotion Added",
        description: `Successfully added ${newPromotion.name} promotion.`,
      });
      
      setIsAddPromoOpen(false);
      form.reset({
        ...form.getValues(),
        id: `PROMO-${Math.floor(1000 + Math.random() * 9000)}`,
      });
      
      // Refresh the list
      const updatedPromotions = await getAllPromotions();
      setPromotions(updatedPromotions);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add promotion.",
      });
    }
  };

  const getPromotionStatus = (startDate: string, endDate: string) => {
    const today = new Date().toISOString().split('T')[0];
    if (startDate > today) return "Upcoming";
    if (endDate < today) return "Expired";
    return "Active";
  };

  const handleRetry = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getAllPromotions();
      setPromotions(data);
      setFilteredPromotions(data);
      setIsLoading(false);
      toast({
        title: "Data Refreshed",
        description: "Successfully refreshed promotion data.",
      });
    } catch (error) {
      console.error("Failed to retry fetching promotions", error);
      setError("Failed to load promotion data. Please try refreshing the page.");
      toast({
        variant: "destructive",
        title: "Fetch Error",
        description: "Failed to fetch promotion data on retry.",
      });
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <LayoutLoader message="Loading promotions..." />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <h2 className="text-xl font-semibold text-red-600 mb-4">{error}</h2>
        <p className="text-muted-foreground mb-6">There was an issue retrieving the promotion data.</p>
        <Button onClick={handleRetry}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Promotions</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {promotions.filter(p => {
                const today = new Date().toISOString().split('T')[0];
                return p.startDate <= today && p.endDate >= today;
              }).length}
            </div>
            <p className="text-xs text-muted-foreground">
              Currently running promotional campaigns
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Redemptions</CardTitle>
            <Tag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {promotions.reduce((acc, curr) => acc + curr.currentRedemptions, 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Across all promotional campaigns
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Coming Soon</CardTitle>
            <CalendarClock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {promotions.filter(p => {
                const today = new Date().toISOString().split('T')[0];
                return p.startDate > today;
              }).length}
            </div>
            <p className="text-xs text-muted-foreground">
              Scheduled upcoming promotions
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col md:flex-row justify-between gap-4 mb-4">
        <div className="flex flex-1 items-center gap-2">
          <Search className="h-5 w-5 text-muted-foreground" />
          <Input
            placeholder="Search promotions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-sm"
          />
        </div>
        <div>
          <Dialog open={isAddPromoOpen} onOpenChange={setIsAddPromoOpen}>
            <DialogTrigger asChild>
              <Button><Plus className="mr-2 h-4 w-4" />Add Promotion</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Create New Promotion</DialogTitle>
                <DialogDescription>
                  Set up a new promotion for cooperative members.
                </DialogDescription>
              </DialogHeader>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Promotion Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Promotion Name" {...field} required />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Promotion ID</FormLabel>
                          <FormControl>
                            <Input {...field} readOnly />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="col-span-2">
                      <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                              <Input placeholder="Promotion Description" {...field} required />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="startDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Start Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="endDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>End Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="cooperativeName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Cooperative (Optional)</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="All Cooperatives" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="all-coops">All Cooperatives</SelectItem>
                              <SelectItem value="Retail Cooperative A">Retail Cooperative A</SelectItem>
                              <SelectItem value="School Cooperative B">School Cooperative B</SelectItem>
                              <SelectItem value="Agricultural Cooperative C">Agricultural Cooperative C</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="memberGroup"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Member Group (Optional)</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="All Members" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="all-members">All Members</SelectItem>
                              <SelectItem value="Regular">Regular</SelectItem>
                              <SelectItem value="Silver">Silver</SelectItem>
                              <SelectItem value="Gold">Gold</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="discountType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Discount Type</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select discount type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Percentage">Percentage (%)</SelectItem>
                              <SelectItem value="Fixed">Fixed Amount (RM)</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="discountValue"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Discount Value</FormLabel>
                          <FormControl>
                            <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="minimumSpend"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Minimum Spend (RM)</FormLabel>
                          <FormControl>
                            <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="maxRedemptions"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Maximum Redemptions</FormLabel>
                          <FormControl>
                            <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="productCategory"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Product Category (Optional)</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="All Products" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="all-products">All Products</SelectItem>
                              <SelectItem value="Grocery">Grocery</SelectItem>
                              <SelectItem value="School Supplies">School Supplies</SelectItem>
                              <SelectItem value="Electronics">Electronics</SelectItem>
                              <SelectItem value="Clothing">Clothing</SelectItem>
                              <SelectItem value="Home Goods">Home Goods</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <DialogFooter>
                    <Button type="submit">Save Promotion</Button>
                  </DialogFooter>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <span className="text-sm text-muted-foreground">Filter by:</span>
        
        <Select value={coopFilter} onValueChange={setCoopFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Cooperative" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cooperatives</SelectItem>
            <SelectItem value="null">Available to All</SelectItem>
            <SelectItem value="Retail Cooperative A">Retail Cooperative A</SelectItem>
            <SelectItem value="School Cooperative B">School Cooperative B</SelectItem>
            <SelectItem value="Agricultural Cooperative C">Agricultural Cooperative C</SelectItem>
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="upcoming">Upcoming</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableCaption>List of member promotions</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Min. Spend</TableHead>
              <TableHead>Start Date</TableHead>
              <TableHead>End Date</TableHead>
              <TableHead>Cooperative</TableHead>
              <TableHead>Member Group</TableHead>
              <TableHead>Redemptions</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredPromotions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-4">No promotions found</TableCell>
              </TableRow>
            ) : (
              filteredPromotions.map((promo) => (
                <TableRow key={promo.id}>
                  <TableCell className="font-medium">{promo.id}</TableCell>
                  <TableCell>{promo.name}</TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1">
                      <Percent className="h-3 w-3" />
                      {promo.discountType === "Percentage" 
                        ? `${promo.discountValue}%` 
                        : `RM ${promo.discountValue.toFixed(2)}`}
                    </span>
                  </TableCell>
                  <TableCell>RM {promo.minimumSpend.toFixed(2)}</TableCell>
                  <TableCell>{promo.startDate}</TableCell>
                  <TableCell>{promo.endDate}</TableCell>
                  <TableCell>{promo.cooperativeName || "All Cooperatives"}</TableCell>
                  <TableCell>{promo.memberGroup || "All Members"}</TableCell>
                  <TableCell>{promo.currentRedemptions} / {promo.maxRedemptions}</TableCell>
                  <TableCell>
                    <CustomBadge variant={
                      getPromotionStatus(promo.startDate, promo.endDate) === "Active" ? "success" : 
                      getPromotionStatus(promo.startDate, promo.endDate) === "Upcoming" ? "outline" : 
                      "secondary"
                    }>
                      {getPromotionStatus(promo.startDate, promo.endDate)}
                    </CustomBadge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default MemberPromotionsTab;
